import React, { useMemo, useState } from 'react';
import {
  useFetchOpportunityArchitectures,
  useFetchOpportunityChallenges,
  useFetchOpportunityOutcomes,
  useFetchCustomer,
  useFetchOpportunity,
  useAddOpportunityChallengeMutation,
  useDeleteOpportunityChallengeMutation,
  useAddOpportunityOutcomeMutation,
  useDeleteOpportunityOutcomeMutation,
  useUpdateOpportunityMutation,
  useAddOpportunityArchitectureMutation,
  useDeleteOpportunityArchitectureMutation,
} from '../../../../hooks/opportunity-hooks';
import { useFetchUser } from '../../../../hooks/user-hooks';
import {
  HbrOption,
  HbrSelect,
  HbrSpinner,
} from '@harbor/elements-react-standalone';
import { useParams } from 'react-router-dom';
import { useArchitectures, useChallenges, useOutcomes } from '../../../../hooks/useToolboxHooks';
import { Challenge, Outcome } from '../../../../types/customer';
import { Architecture } from '../../../../types/product';
import ListWithMultiSelect from '../../../Common/ListWithMultiSelect';
import ListWithGroupedMultiSelect from '../../../Common/ListWithGroupedMultiSelect';
import OpportunityPhaseLayout from '../OpportunityPhaseLayout';
import OpportunityPhaseCard from '../OpportunityPhaseCard';

const solutionTypeOptions = [
  { value: '', label: 'Select Solution Type' },
  { value: 'incumbent', label: 'Incumbent' },
  { value: 'displace', label: 'Displace Competitor' },
  { value: 'new', label: 'New Solution' },
];

const priorityOptions = [
  { value: 'not a priority', label: 'Not Priority' },
  { value: 'a minimal priority', label: 'Minimal Priority' },
  { value: 'somewhat a priority', label: 'Somewhat Priority' },
  { value: 'a significant priority', label: 'Significant Priority' },
  { value: 'a critical priority', label: 'Critical Priority' },
];

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

const StatusText: React.FC<{ state?: SaveState }> = ({ state }) => {
  if (state === 'saving') return <span className="text-xs text-gray-500">Saving...</span>;
  if (state === 'saved') return <span className="text-xs text-green-600">Saved</span>;
  if (state === 'error') return <span className="text-xs text-red-600">Failed to save</span>;
  return <span className="text-xs text-transparent">.</span>;
};

const DiscoverPhase = () => {
  const { id: opportunityId } = useParams<{ id: string }>();

  const { data: oppData } = useFetchOpportunity(opportunityId, { enabled: !!opportunityId });
  const { mutateAsync: updateOpportunity } = useUpdateOpportunityMutation();

  const { data: user } = useFetchUser();
  const { data: opportunity } = useFetchOpportunity(opportunityId, { enabled: !!opportunityId });

  useFetchCustomer(oppData?.AccountId || opportunity?.AccountId);

  const { architectures } = useArchitectures();
  const { data: opportunityArchitectures } = useFetchOpportunityArchitectures(opportunityId, { enabled: !!opportunityId });
  const { mutateAsync: addArchitecture, isPending: isAddingArchitecture } = useAddOpportunityArchitectureMutation();
  const { mutateAsync: deleteArchitecture, isPending: isDeletingArchitecture } = useDeleteOpportunityArchitectureMutation();
  const loadingArchitecture = isAddingArchitecture || isDeletingArchitecture;

  const { challenges } = useChallenges();
  const { data: opportunityChallenges } = useFetchOpportunityChallenges(opportunityId, { enabled: !!opportunityId });
  const { mutateAsync: addChallenge, isPending: isAddingChallenge } = useAddOpportunityChallengeMutation();
  const { mutateAsync: deleteChallenge, isPending: isDeletingChallenge } = useDeleteOpportunityChallengeMutation();
  const loadingChallenge = isAddingChallenge || isDeletingChallenge;

  const { outcomes } = useOutcomes();
  const { data: opportunityOutcomes } = useFetchOpportunityOutcomes(opportunityId, { enabled: !!opportunityId });
  const { mutateAsync: addOutcome, isPending: isAddingOutcome } = useAddOpportunityOutcomeMutation();
  const { mutateAsync: deleteOutcome, isPending: isDeletingOutcome } = useDeleteOpportunityOutcomeMutation();
  const loadingOutcome = isAddingOutcome || isDeletingOutcome;

  const [fieldSaveState, setFieldSaveState] = useState<Record<string, SaveState>>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  const safeChallenges: Challenge[] = useMemo(
    () => (challenges ?? []).filter((c: any) => c && c.id != null),
    [challenges]
  );

  const safeOutcomes: Outcome[] = useMemo(
    () => (outcomes ?? []).filter((o: any) => o && o.id != null),
    [outcomes]
  );

  const safeArchitectures: Architecture[] = useMemo(
    () => (architectures ?? []).filter((a: any) => a && a.id != null),
    [architectures]
  );

  const safeSelectedChallenges: Challenge[] = useMemo(
    () => (opportunityChallenges ?? []).map((r: any) => r?.challenge).filter(Boolean),
    [opportunityChallenges]
  );

  const safeSelectedOutcomes: Outcome[] = useMemo(
    () => (opportunityOutcomes ?? []).map((r: any) => r?.outcome).filter(Boolean),
    [opportunityOutcomes]
  );

  const selectedArchitectureIds = useMemo(
    () => (opportunityArchitectures ?? []).map((x: any) => x?.architecture).filter(Boolean),
    [opportunityArchitectures]
  );

  const selectedArchitectures = useMemo(
    () => safeArchitectures.filter((a) => selectedArchitectureIds.map(String).includes(String(a.id))),
    [safeArchitectures, selectedArchitectureIds]
  );

  const formData = useMemo(
    () => ({
      priority: oppData?.organizational_priority || '',
      solutionType: oppData?.solution_type || '',
    }),
    [oppData]
  );

  const setFieldStateTemporarilySaved = (fieldName: string) => {
    setFieldSaveState((prev) => ({ ...prev, [fieldName]: 'saved' }));
    setTimeout(() => {
      setFieldSaveState((prev) => ({ ...prev, [fieldName]: 'idle' }));
    }, 1200);
  };

  const syncRelations = async <T extends { id: number | string }>({
                                                                    selectedItems,
                                                                    currentIds,
                                                                    allItems,
                                                                    relations,
                                                                    addFn,
                                                                    deleteFn,
                                                                    buildAddPayload,
                                                                    getRelationTargetId,
                                                                    relationDeletePayload,
                                                                  }: {
    selectedItems: T[];
    currentIds: string[];
    allItems: T[];
    relations: any[];
    addFn: (payload: any) => Promise<any>;
    deleteFn: (payload: any) => Promise<any>;
    buildAddPayload: (item: T) => any;
    getRelationTargetId: (relation: any) => string;
    relationDeletePayload: (relation: any) => any;
  }) => {
    const selectedIds = (selectedItems ?? []).map((item) => String(item.id));
    const toAdd = selectedIds.filter((id) => !currentIds.includes(id));
    const toRemove = currentIds.filter((id) => !selectedIds.includes(id));

    for (const itemId of toAdd) {
      const item = allItems.find((x) => String(x.id) === itemId);
      if (!item) continue;
      await addFn(buildAddPayload(item));
    }

    for (const itemId of toRemove) {
      const rels = (relations ?? []).filter((rel) => getRelationTargetId(rel) === itemId);
      for (const rel of rels) {
        if (rel?.id) {
          await deleteFn(relationDeletePayload(rel));
        }
      }
    }
  };

  const handleChallengeUpdates = async (selectedChallenges: Challenge[]): Promise<void> => {
    try {
      setSaveError(null);

      await syncRelations({
        selectedItems: selectedChallenges,
        currentIds: safeSelectedChallenges.map((c) => String(c.id)),
        allItems: safeChallenges,
        relations: opportunityChallenges ?? [],
        addFn: addChallenge,
        deleteFn: deleteChallenge,
        buildAddPayload: (challengeObj) => ({
          updated_by: user?.email,
          opportunity: oppData,
          challenge: challengeObj,
        }),
        getRelationTargetId: (rel) => String(rel?.challenge?.id),
        relationDeletePayload: (rel) => ({ opportunity: opportunityId, id: rel.id }),
      });
    } catch (err) {
      console.error('Error updating challenges:', err);
      setSaveError('Failed to update challenges');
      throw err;
    }
  };

  const handleOutcomeUpdates = async (selectedOutcomes: Outcome[]): Promise<void> => {
    try {
      setSaveError(null);

      await syncRelations({
        selectedItems: selectedOutcomes,
        currentIds: safeSelectedOutcomes.map((o) => String(o.id)),
        allItems: safeOutcomes,
        relations: opportunityOutcomes ?? [],
        addFn: addOutcome,
        deleteFn: deleteOutcome,
        buildAddPayload: (outcomeObj) => ({
          updated_by: user?.email,
          opportunity: oppData,
          outcome: outcomeObj,
        }),
        getRelationTargetId: (rel) => String(rel?.outcome?.id),
        relationDeletePayload: (rel) => ({ opportunity: opportunityId, id: rel.id }),
      });
    } catch (err) {
      console.error('Error updating outcomes:', err);
      setSaveError('Failed to update outcomes');
      throw err;
    }
  };

  const handleArchitectureUpdates = async (selectedItems: Architecture[]): Promise<void> => {
    try {
      setSaveError(null);

      await syncRelations({
        selectedItems,
        currentIds: selectedArchitectureIds.map(String),
        allItems: safeArchitectures,
        relations: opportunityArchitectures ?? [],
        addFn: addArchitecture,
        deleteFn: deleteArchitecture,
        buildAddPayload: (archObj) => ({
          updated_by: user?.email,
          opportunity: oppData?.id,
          last_updated: new Date().toISOString(),
          architecture: archObj.id,
        }),
        getRelationTargetId: (rel) => String(rel?.architecture),
        relationDeletePayload: (rel) => ({ opportunity: opportunityId, id: rel.id }),
      });
    } catch (err) {
      console.error('Error updating architectures:', err);
      setSaveError('Failed to update architectures');
      throw err;
    }
  };

  const handleChange = async (name: 'priority' | 'solutionType', value: string) => {
    try {
      setSaveError(null);
      setFieldSaveState((prev) => ({ ...prev, [name]: 'saving' }));

      await updateOpportunity({
        id: opportunityId,
        customer_id: oppData?.AccountId,
        organizational_priority: name === 'priority' ? value : formData.priority,
        solution_type: name === 'solutionType' ? value : formData.solutionType,
        init_config: true,
        updated_by: user?.email,
      });

      setFieldStateTemporarilySaved(name);
    } catch (error) {
      console.error('Error saving opportunity:', error);
      setFieldSaveState((prev) => ({ ...prev, [name]: 'error' }));
      setSaveError('Failed to save opportunity details');
    }
  };

  if (!opportunityId || !oppData) {
    return (
      <div className="py-8 flex justify-center">
        <HbrSpinner size="large">
          <div slot="label">Loading discovery details...</div>
        </HbrSpinner>
      </div>
    );
  }

  return (
    <OpportunityPhaseLayout
      title="Discovery Phase"
      subtitle="Capture customer drivers, expected outcomes, relevant architectures, and qualification details."
    >
      {saveError && (
        <div className="mb-4 text-sm text-red-600">{saveError}</div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.5fr)_minmax(320px,0.95fr)] gap-4 items-start">
        <div className="flex flex-col gap-4">
          <OpportunityPhaseCard
            title="Customer Challenges"
            actions={loadingChallenge ? <HbrSpinner size="small" /> : null}
          >
            <div className="text-sm text-gray-600 mb-3">
              What key pain points or blockers are driving this opportunity?
            </div>

            <ListWithGroupedMultiSelect
              title=""
              selectedItems={safeSelectedChallenges}
              optionItems={safeChallenges}
              typeLabel="Challenges"
              loading={loadingChallenge}
              onClick={handleChallengeUpdates}
            >
              {(item: Challenge, index: number) => (
                <li key={index}>
                  <h4 className="mb-0 font-bold text-sm">{item?.name ?? ''}</h4>
                  {item?.description && <p className="text-sm mb-0">{item.description}</p>}
                </li>
              )}
            </ListWithGroupedMultiSelect>
          </OpportunityPhaseCard>

          <OpportunityPhaseCard
            title="Desired Outcomes"
            actions={loadingOutcome ? <HbrSpinner size="small" /> : null}
          >
            <div className="text-sm text-gray-600 mb-3">
              What outcomes should the customer achieve if this opportunity succeeds?
            </div>

            <ListWithGroupedMultiSelect
              title=""
              selectedItems={safeSelectedOutcomes}
              optionItems={safeOutcomes}
              typeLabel="Outcomes"
              loading={loadingOutcome}
              onClick={handleOutcomeUpdates}
            >
              {(item: Outcome, index: number) => (
                <li key={index}>
                  <h4 className="mb-0 font-bold text-sm">{item?.name ?? ''}</h4>
                  {item?.description && <p className="text-sm mb-0">{item.description}</p>}
                </li>
              )}
            </ListWithGroupedMultiSelect>
          </OpportunityPhaseCard>
        </div>

        <div className="flex flex-col gap-4">
          <OpportunityPhaseCard title="Opportunity Qualification">
            <div className="flex flex-col gap-4">
              <div>
                <div className="text-sm font-medium mb-2">Customer Priority</div>
                <HbrSelect
                  name="priority"
                  value={oppData?.organizational_priority ?? ''}
                  clearable
                  onHbr-change={(e: any) => handleChange('priority', e.target?.value ?? '')}
                >
                  {priorityOptions.map((opt) => (
                    <HbrOption key={opt.value} value={opt.value}>
                      {opt.label}
                    </HbrOption>
                  ))}
                </HbrSelect>
                <div className="mt-1 min-h-[16px]">
                  <StatusText state={fieldSaveState.priority} />
                </div>
              </div>

              <div>
                <div className="text-sm font-medium mb-2">Solution Type</div>
                <HbrSelect
                  name="solutionType"
                  value={formData.solutionType}
                  clearable
                  onHbr-change={(e: any) => handleChange('solutionType', e.target?.value ?? '')}
                >
                  {solutionTypeOptions.map((opt) => (
                    <HbrOption key={opt.value} value={opt.value}>
                      {opt.label}
                    </HbrOption>
                  ))}
                </HbrSelect>
                <div className="mt-1 min-h-[16px]">
                  <StatusText state={fieldSaveState.solutionType} />
                </div>
              </div>
            </div>
          </OpportunityPhaseCard>

          <OpportunityPhaseCard
            title="Relevant Architectures"
            actions={loadingArchitecture ? <HbrSpinner size="small" /> : null}
          >
            <div className="text-sm text-gray-600 mb-3">
              Which architecture patterns or solution structures are relevant here?
            </div>

            <ListWithMultiSelect
              title=""
              selectedItems={selectedArchitectures}
              optionItems={safeArchitectures}
              typeLabel="Architectures"
              loading={loadingArchitecture}
              onClick={handleArchitectureUpdates}
            >
              {(item: Architecture, index: number) => (
                <li key={index}>
                  <h4 className="mb-0 font-bold text-sm">{item?.name ?? ''}</h4>
                </li>
              )}
            </ListWithMultiSelect>
          </OpportunityPhaseCard>
        </div>
      </div>
    </OpportunityPhaseLayout>
  );
};

export default DiscoverPhase;